import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Users, Lightbulb, CheckCircle2, BarChart3, Layers, Compass, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import LeadForm from '@/components/shared/LeadForm';

const SERVICES = [
  {
    id: 'vision-alignment',
    icon: Compass,
    title: 'Vision & Alignment',
    tagline: 'Create shared vision and strategic alignment',
    description: 'Help leaders articulate an aspirational state for the organization and achieve alignment where purpose, strategy, goals, and objectives strengthen each other for transformational success.',
    approach: [
      { step: 'Reality Check', detail: 'Discuss current reality, accomplishments, and learning from past initiatives' },
      { step: 'Drafting the Vision', detail: 'Brainstorming and creating a clear, concise vision for the next 2-3 years' },
      { step: 'Identify Factors', detail: 'Force Field Analysis to determine what helps or hinders vision achievement' },
      { step: 'Key Focus Areas', detail: 'Determine critical areas that will help achieve the vision' },
      { step: 'Action Planning', detail: 'Develop individual and team action plans to overcome limitations' }
    ],
    deliverables: ['Visioning Workshop facilitation', 'Strategic alignment sessions', 'Force Field Analysis', 'Action plans for implementation'],
    outcomes: 'Strategic clarity, aligned leadership, shared purpose, actionable roadmap',
    caseStudy: { client: 'Retail Chain', result: '70% revenue growth over 3 years through vision alignment' }
  },
  {
    id: 'org-structure',
    icon: Layers,
    title: 'Organization Structure',
    tagline: 'Design efficient structures for growth',
    description: 'Work with organizations to create efficient and effective structures where decision-making and accountability are well-defined, and communication flows naturally.',
    approach: [
      { step: 'Business Vision Understanding', detail: 'One-on-one meetings to gain clarity on history, vision, and business model' },
      { step: 'Manpower Review', detail: 'Analyze org structure, span of control, reporting matrix, job roles, KRAs & KPIs' },
      { step: 'Industry Benchmarking', detail: 'Study similar organizations\' structures, roles, and skills' },
      { step: 'Performance & Cost Analysis', detail: 'Review existing performance, costs, expectations, and contribution levels' },
      { step: 'Restructuring', detail: 'Create solutions, restructure, enhance roles, set KRAs/KPIs' },
      { step: 'Re-Engagement', detail: 'Initiate communication, expectation setting, and advise reward strategies' }
    ],
    deliverables: ['Org design & restructuring', 'Role clarity & KRA/KPI setting', 'Span of control optimization', 'Reporting matrix design'],
    outcomes: 'Optimized structure, clear roles, improved efficiency, natural communication flow',
    caseStudy: { client: 'Manufacturing Firm', result: '₹70 Cr → ₹200+ Cr revenue in 3 years post-restructure' }
  },
  {
    id: 'org-diagnostic',
    icon: Target,
    title: 'Organization Diagnostic',
    tagline: 'Assess and identify transformation opportunities',
    description: 'Proprietary diagnostic tool to assess family-owned businesses, identify challenges, and improve organizational performance based on ten fundamental questions.',
    approach: [
      { step: 'Purpose & Vision Assessment', detail: 'Evaluate clarity of mission, goals, and key focus areas' },
      { step: 'Management Systems', detail: 'Assess processes and systems to implement strategies effectively' },
      { step: 'Culture & Values', detail: 'Examine management styles, attitudes, values, and sources of conflict' },
      { step: 'Top Management Evaluation', detail: 'Assess strategic thinking and direction from leadership' },
      { step: 'Overall Org Health', detail: 'Evaluate strengths, weaknesses, and readiness for development' }
    ],
    deliverables: ['10-point business assessment', 'Culture & management analysis', 'Readiness evaluation', 'Transformation roadmap'],
    outcomes: 'Identified gaps, actionable insights, transformation foundation, competitive edge',
    caseStudy: { client: 'Diamond Jewellery Manufacturer', result: '13.3% production cost reduction after diagnostic-driven changes' }
  },
  {
    id: 'performance-management',
    icon: BarChart3,
    title: 'Performance Management',
    tagline: 'Maximize people power through performance',
    description: 'Design and implement the Performance Dialogue Optimizer to enable organizations to monitor, manage, and improve execution and deliver results.',
    approach: [
      { step: 'Pre-work & Visioning', detail: 'Understand strategy and identify key focus areas for performance' },
      { step: 'Job Descriptions & KPIs', detail: 'Draft clear JDs, KRAs, and KPIs aligned with organizational goals' },
      { step: 'System Creation', detail: 'Define competencies, review mechanisms, design forms, plan compensation' },
      { step: 'Implementation', detail: 'Educate organization, translate responsibilities into measurable form' },
      { step: 'Handholding & Support', detail: 'Monitor process and create course corrections as needed' },
      { step: 'Ongoing Review', detail: 'Assess effectiveness and refine system continuously' }
    ],
    deliverables: ['Performance Dialogue Optimizer', 'KRA/KPI framework', 'Competency design', 'Review mechanisms & forms'],
    outcomes: 'Maximized performance, execution excellence, results-driven culture, coaching mindset',
    caseStudy: { client: 'Agri-Tech Company', result: '62% → 77% engagement score in 12 months' }
  },
  {
    id: 'org-values',
    icon: Lightbulb,
    title: 'Organization Values',
    tagline: 'Create the organizational heartbeat',
    description: 'Find the shared values of your organization and implement strategies to live those values, shaping culture and influencing communication, relationships, and decisions.',
    approach: [
      { step: 'Awareness', detail: 'Create clarity about what cultural change is needed' },
      { step: 'Alignment', detail: 'Foster commitment from leaders and the broader organization' },
      { step: 'Adaptability', detail: 'Produce sustainability of ongoing change and innovation' },
      { step: 'Assessment', detail: 'Help employees understand and demonstrate organizational values' }
    ],
    deliverables: ['Values framework creation', 'Behavioral definitions (acceptable/unacceptable)', 'Implementation strategy', 'Recognition programs'],
    outcomes: 'Stronger culture, aligned behaviors, better decision-making, improved results',
    caseStudy: { client: 'Retail Brand', result: '130% → 34% attrition after values-driven culture shift' }
  },
  {
    id: 'succession-planning',
    icon: Users,
    title: 'Succession Planning',
    tagline: 'Prepare the organization for tomorrow',
    description: 'Carefully craft succession plans that look into the future and develop strong leaders and successors for pivotal roles through a rigorous five-fold approach.',
    approach: [
      { step: 'Identify Critical Roles', detail: 'Based on value creation, urgency, and business impact' },
      { step: 'Build Role Profiles', detail: 'Articulate role requirements, KPIs, and unique skillsets' },
      { step: 'Nominate Successors', detail: 'Determine measurable criteria to evaluate candidate potential' },
      { step: 'Assess Development Needs', detail: 'Create talent development plan for each potential successor' },
      { step: 'Develop Talent Pipeline', detail: 'Draft IDPs to bridge development gaps' },
      { step: 'Transition Planning', detail: 'Build structured transition plan for new roles' },
      { step: 'Re-balance Bench', detail: 'Evaluate bench strength and shift to demand-driven planning' }
    ],
    deliverables: ['Critical role identification', 'Talent assessment', 'Individual Development Plans', 'Transition planning'],
    outcomes: 'Leadership pipeline, business continuity, bench strength, reduced succession risk',
    caseStudy: { client: 'Plastics Processor', result: '11 leaders developed, ₹1.95 Cr annual savings' }
  }
];

export default function ConsultingPage() {
  const [expandedService, setExpandedService] = useState(null);

  return (
    <div className="bg-[#FAF5EF]">
      {/* Hero */}
      <section className="relative py-24 lg:py-32 bg-gradient-to-br from-[#1A1008] to-[#3D1C11] text-white">
        <div className="absolute inset-0 opacity-[0.03]">
          <div className="absolute top-20 right-20 w-96 h-96 bg-[#E8642C] rounded-full blur-3xl" />
        </div>
        
        <div className="relative z-10 mx-auto w-full max-w-[1400px] px-6 sm:px-8 lg:px-12">
          <div className="max-w-[900px]">
            <p className="text-[11px] font-semibold text-[#E8642C] tracking-[0.25em] uppercase mb-6">Strategic Consulting</p>
            <h1 className="text-[clamp(3rem,6vw,5.5rem)] font-bold leading-[1.05] tracking-[-0.02em] mb-8 text-white">
              We don't build strategies on paper.
            </h1>
            <p className="text-2xl text-[#E8642C] font-semibold mb-8">We build organizations that execute them.</p>
            <p className="text-xl text-white/80 leading-relaxed mb-12">
              From vision to execution. Structure to culture. Performance to succession. 
              Six integrated consulting services that transform businesses from the inside out.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <Link to="/contact" className="inline-flex items-center gap-2 bg-[#E8642C] text-white px-8 py-4 rounded-full text-sm font-semibold hover:bg-[#CC2929] transition-all shadow-lg">
                Start Your Transformation
                <ArrowRight size={16} />
              </Link>
              <Link to="/impact" className="inline-flex items-center gap-2 bg-white/10 text-white px-8 py-4 rounded-full text-sm font-semibold hover:bg-white/20 transition-all border border-white/20">
                View Case Studies
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services - Detailed */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="mx-auto w-full max-w-[1400px] px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#1A1008] mb-6">Our Consulting Services</h2>
            <p className="text-lg text-[#6B5B50] max-w-[700px] mx-auto">
              Six comprehensive services to transform your organization. Click to explore each in detail.
            </p>
          </div>

          <div className="space-y-6">
            {SERVICES.map((service, i) => (
              <DetailedServiceCard 
                key={service.id} 
                service={service} 
                index={i}
                isExpanded={expandedService === service.id}
                onToggle={() => setExpandedService(expandedService === service.id ? null : service.id)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Impact */}
      <section className="py-20 lg:py-28 bg-[#FAF5EF]">
        <div className="mx-auto w-full max-w-[1400px] px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#1A1008] mb-6">Proven Impact</h2>
            <p className="text-lg text-[#6B5B50] max-w-[700px] mx-auto">
              Real transformations delivered across industries.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { value: '75%', label: 'Revenue Growth', detail: 'Marble Giant (18 months)' },
              { value: '186%', label: 'Revenue Growth', detail: 'Manufacturing (3 years)' },
              { value: '96%', label: 'Attrition Cut', detail: 'Retail Transformation' }
            ].map((stat, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 text-center shadow-md hover:shadow-xl transition-shadow">
                <div className="text-5xl font-bold text-[#E8642C] mb-3">{stat.value}</div>
                <div className="text-sm font-bold text-[#1A1008] mb-2 uppercase tracking-wide">{stat.label}</div>
                <div className="text-xs text-[#6B5B50]">{stat.detail}</div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/impact" className="inline-flex items-center gap-2 text-[#E8642C] font-semibold hover:gap-3 transition-all">
              View all transformation stories
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="mx-auto w-full max-w-[1000px] px-6 sm:px-8 lg:px-12">
          <div className="bg-gradient-to-br from-[#FAF5EF] to-[#FFF7EF] rounded-3xl p-10 lg:p-16 border border-[#3D1C11]/10">
            <div className="text-center mb-10">
              <h2 className="text-3xl lg:text-4xl font-bold text-[#1A1008] mb-6">
                Ready to transform your organization?
              </h2>
              <p className="text-lg text-[#6B5B50] max-w-[600px] mx-auto">
                Let's discuss how our consulting expertise can deliver measurable impact for your business.
              </p>
            </div>
            <LeadForm source="consulting_page" />
          </div>
        </div>
      </section>
    </div>
  );
}

function DetailedServiceCard({ service, index, isExpanded, onToggle }) {
  const { ref, inView } = useInView({ threshold: 0.15, triggerOnce: true });
  const Icon = service.icon;
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.05, ease: [0.16, 1, 0.3, 1] }}
      className="bg-white rounded-3xl border border-[#3D1C11]/10 hover:border-[#E8642C]/30 transition-all overflow-hidden"
    >
      <button
        onClick={onToggle}
        className="w-full p-8 lg:p-10 flex items-start gap-6 text-left hover:bg-[#FAF5EF]/50 transition-colors"
      >
        <div className="flex-shrink-0 w-14 h-14 bg-[#E8642C]/10 rounded-2xl flex items-center justify-center">
          <Icon className="w-7 h-7 text-[#E8642C]" />
        </div>
        
        <div className="flex-1">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-2xl font-bold text-[#1A1008] mb-2">{service.title}</h3>
              <p className="text-sm text-[#E8642C] font-medium mb-3">{service.tagline}</p>
              <p className="text-[#6B5B50] leading-relaxed">{service.description}</p>
            </div>
            <motion.div
              animate={{ rotate: isExpanded ? 180 : 0 }}
              transition={{ duration: 0.3 }}
              className="flex-shrink-0"
            >
              <ChevronDown className="w-6 h-6 text-[#6B5B50]" />
            </motion.div>
          </div>
        </div>
      </button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="px-8 lg:px-10 pb-10 pt-4 border-t border-[#3D1C11]/10 bg-[#FAF5EF]/30">
              <div className="grid lg:grid-cols-2 gap-10">
                {/* Approach */}
                <div>
                  <h4 className="text-lg font-bold text-[#1A1008] mb-6 flex items-center gap-2">
                    <div className="w-1 h-5 bg-[#E8642C] rounded-full" />
                    Our Approach
                  </h4>
                  <div className="space-y-4">
                    {service.approach.map((step, i) => (
                      <div key={i} className="flex gap-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-[#E8642C]/10 rounded-lg flex items-center justify-center text-[#E8642C] font-bold text-sm">
                          {i + 1}
                        </div>
                        <div>
                          <div className="font-bold text-[#1A1008] text-sm mb-1">{step.step}</div>
                          <div className="text-xs text-[#6B5B50] leading-relaxed">{step.detail}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Deliverables & Outcomes */}
                <div>
                  <h4 className="text-lg font-bold text-[#1A1008] mb-6 flex items-center gap-2">
                    <div className="w-1 h-5 bg-[#E8642C] rounded-full" />
                    Deliverables
                  </h4>
                  <div className="space-y-2 mb-8">
                    {service.deliverables.map((item, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-[#E8642C] flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-[#6B5B50]">{item}</span>
                      </div>
                    ))}
                  </div>

                  <h4 className="text-lg font-bold text-[#1A1008] mb-4 flex items-center gap-2">
                    <div className="w-1 h-5 bg-[#E8642C] rounded-full" />
                    Expected Outcomes
                  </h4>
                  <p className="text-sm text-[#E8642C] font-medium mb-6">{service.outcomes}</p>

                  {service.caseStudy && (
                    <div className="bg-white rounded-xl p-5 border border-[#E8642C]/20">
                      <div className="text-xs font-bold text-[#1A1008] uppercase tracking-wide mb-2">Case Study</div>
                      <div className="text-sm text-[#6B5B50]">
                        <span className="font-semibold text-[#1A1008]">{service.caseStudy.client}:</span> {service.caseStudy.result}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}